import { useEffect } from 'react';
import { useDispatch } from 'react-redux';

import {
  FARM_DIAGNOSTICS_TIME_RANGE_ALL,
  FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM,
  FARM_DIAGNOSTICS_TIME_RANGE_LAST_3,
  FARM_DIAGNOSTICS_TIME_RANGE_LAST_5,
  PLAN_PREVIEW_DIAGNOSTICS_SNAPSHOT_STORAGE_KEY,
} from 'constants/uiConstants';

import {
  hydrateFarmDiagnosticsSnapshot,
  type FarmDiagnosticsSnapshot,
  type FarmDiagnosticsTimeRange,
  type InsightsState,
} from '../../redux/slices/InsightsSlice';

/*
 * Hands the workspace's Yearly Overview (rainfall, practice, what-ifs,
 * Plan/Actual choices, time ranges) to a plan preview opened in a new tab
 * (OP-2490). That state is session-only Redux, which a new tab starts
 * without.
 *
 * sessionStorage is copied into a tab opened with window.open, so writing it
 * just before opening is enough. It stays with that tab — it survives a
 * refresh of the preview and is gone once the tab closes. Nothing is sent to
 * the server.
 */

const storageKey = (orgId: string) =>
  `${PLAN_PREVIEW_DIAGNOSTICS_SNAPSHOT_STORAGE_KEY}:${orgId}`;

const TIME_RANGE_MODES = new Set<string>([
  FARM_DIAGNOSTICS_TIME_RANGE_LAST_3,
  FARM_DIAGNOSTICS_TIME_RANGE_LAST_5,
  FARM_DIAGNOSTICS_TIME_RANGE_ALL,
  FARM_DIAGNOSTICS_TIME_RANGE_CUSTOM,
]);

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const isYearOrNull = (value: unknown) =>
  value === null || (typeof value === 'number' && Number.isInteger(value));

const isTimeRange = (value: unknown): value is FarmDiagnosticsTimeRange =>
  isRecord(value) &&
  typeof value.mode === 'string' &&
  TIME_RANGE_MODES.has(value.mode) &&
  isYearOrNull(value.customStartYear) &&
  isYearOrNull(value.customEndYear);

/** Every value one level down is itself a year-keyed record. */
const isNestedRecord = (value: unknown) =>
  isRecord(value) && Object.values(value).every(isRecord);

export const writeDiagnosticsSnapshot = (
  orgId: string,
  insights: InsightsState
): void => {
  const snapshot: FarmDiagnosticsSnapshot = {
    farmYearInputs: insights.farmYearInputs,
    farmDiagnosticsOverrides: insights.farmDiagnosticsOverrides,
    farmDiagnosticsCategory: insights.farmDiagnosticsCategory,
    farmDiagnosticsTimeRange: insights.farmDiagnosticsTimeRange,
    rotationTimeRange: insights.rotationTimeRange,
  };

  try {
    sessionStorage.setItem(storageKey(orgId), JSON.stringify(snapshot));
  } catch {
    // Storage unavailable — the preview opens with no Yearly Overview data
    // and its diagnostics sections show their empty state.
  }
};

const readDiagnosticsSnapshot = (
  orgId: string
): FarmDiagnosticsSnapshot | null => {
  try {
    const raw = sessionStorage.getItem(storageKey(orgId));
    if (!raw) return null;

    const parsed: unknown = JSON.parse(raw);
    if (
      !isRecord(parsed) ||
      !isNestedRecord(parsed.farmYearInputs) ||
      !isNestedRecord(parsed.farmDiagnosticsOverrides) ||
      !isNestedRecord(parsed.farmDiagnosticsCategory) ||
      !isTimeRange(parsed.farmDiagnosticsTimeRange) ||
      !isTimeRange(parsed.rotationTimeRange)
    ) {
      return null;
    }

    return parsed as unknown as FarmDiagnosticsSnapshot;
  } catch {
    return null;
  }
};

/** Loads the snapshot for `orgId`, if the workspace left one, into this tab's Redux. */
export const useHydrateDiagnosticsSnapshot = (
  orgId: string | null | undefined
): void => {
  const dispatch = useDispatch();

  useEffect(() => {
    if (!orgId) return;
    const snapshot = readDiagnosticsSnapshot(orgId);
    if (snapshot) dispatch(hydrateFarmDiagnosticsSnapshot(snapshot));
  }, [orgId, dispatch]);
};
